import type { IconSvgProps } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { EmailRouteResolved } from '../lib/emailRoutes';
import { createMutation } from '../lib/mutation';
import { RouteAddressForm } from './RouteAddressForm';
import { RouteDestView } from './RouteDestView';
import { Spinner } from './Spinner';
import { Button } from './ui/Button';

// One .row-list row of the email-routing matrix (#325). Container: owns local
// edit state and persistence wiring; RouteDestView renders the read-mode text
// block, RouteAddressForm the inline override editor. Every category always
// resolves to an override or the account default — there is no mute, so
// `escalate.security` (locked) is re-routable but never silenced.

export type RouteCadence = 'immediate' | 'weekly';

export type EmailRouteCategoryMeta = {
  readonly key: string;
  readonly descKey: string;
  readonly icon: (props: IconSvgProps) => JSX.Element;
  readonly cadence: RouteCadence;
  readonly locked?: boolean;
};

export function EmailRouteRow(props: {
  meta: EmailRouteCategoryMeta;
  resolved: EmailRouteResolved;
  canEdit: boolean;
  onSave: (category: string, address: string) => Promise<void>;
  onReset: (category: string) => Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = createSignal(false);
  const [address, setAddress] = createSignal('');
  const Icon = props.meta.icon;

  const saveRoute = createMutation({
    call: (addr: string) => props.onSave(props.meta.key, addr),
    fallback: t('email_routes.save_error'),
    onSuccess: () => setEditing(false),
  });
  const resetRoute = createMutation({
    call: () => props.onReset(props.meta.key),
    fallback: t('email_routes.reset_error'),
  });

  // The row renders ONE busy state and ONE failure line for both writes, as it
  // did when they shared a signal — the two mutations own their own state, the
  // row folds them back for the markup that never distinguished them.
  const busy = (): boolean => saveRoute.pending() || resetRoute.pending();
  const error = (): string | null => saveRoute.error() ?? resetRoute.error();

  const openEdit = (): void => {
    setAddress(props.resolved.is_override ? (props.resolved.destination ?? '') : '');
    saveRoute.clearError();
    resetRoute.clearError();
    setEditing(true);
  };

  const save = (e: Event): void => {
    e.preventDefault();
    const addr = address().trim();
    if (!addr) return;
    void saveRoute.run(addr);
  };

  const reset = (): void => void resetRoute.run();

  return (
    <li class="row-list__row email-route" data-locked={props.meta.locked ? 'true' : 'false'}>
      <div class="row-list__lead">
        <span class="row-list__tile" aria-hidden="true">
          <Icon size="1rem" />
        </span>
        <RouteDestView meta={props.meta} resolved={props.resolved} editing={editing()} />
      </div>
      <Show
        when={editing()}
        fallback={
          <Show when={props.canEdit}>
            <div class="email-route__actions">
              <Show when={error()}>
                <span class="field__error" role="alert">
                  {error()}
                </span>
              </Show>
              <Button variant="ghost" size="sm" disabled={busy()} onClick={openEdit}>
                {t(props.resolved.is_override ? 'email_routes.edit' : 'email_routes.set_override')}
              </Button>
              <Show when={props.resolved.is_override}>
                <Button variant="ghost" size="sm" disabled={busy()} onClick={reset}>
                  <Show when={busy()} fallback={t('email_routes.reset')}>
                    <Spinner size="0.875rem" />
                    {t('email_routes.resetting')}
                  </Show>
                </Button>
              </Show>
            </div>
          </Show>
        }
      >
        <RouteAddressForm
          category={props.meta.key}
          address={address()}
          busy={busy()}
          error={error()}
          onInput={setAddress}
          onSubmit={save}
          onCancel={() => setEditing(false)}
        />
      </Show>
    </li>
  );
}
